import { OverlayAlertService, OverlayAlertConfig, OverlayAlertRef } from '@gridscale/ingrid/components/overlay-alert';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgrxService } from './../../../../../../app/@shared/services/ngrx.service';
import { AccountService } from './../../../../../../app/@shared/services/account.service';
import { ToastService } from './../../../../../../app/@shared/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as VirtualSwitchActions from './../../../../../../app/@store/virtual_switches/virtual_switches.actions';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { InputTextModule } from '@js/primeng17/inputtext';
import { SharedModule } from '@js/primeng17/api';
import { Store } from '@ngrx/store';
import { selectRouteParam } from './../../../../../../app/@store/router/router.selector';
import * as AccountActions from './../../../../../../app/@store/account/account.actions';
import * as AccountSelectors from './../../../../../../app/@store/account/account.selectors';
import * as _ from 'lodash';
import { AutocompleteComponent, AutocompleteSuggestion } from '@gridscale/ingrid/components/autocomplete';
import { FormValidationErrorsComponent } from '@gridscale/ingrid/components/form-validation-errors';

@Component({
    selector: 'app-create-virtual-switch',
    templateUrl: './create-virtual-switch.component.html',
    styleUrls: ['./create-virtual-switch.component.scss'],
    imports: [
        AutocompleteComponent,
        InputTextModule,
        ReactiveFormsModule,
        SharedModule,
        TranslateModule,
        FormValidationErrorsComponent
    ]
})
export class CreateVirtualSwitchComponent implements OnInit, OnDestroy {
  createForm = this.fb.group({
    name: ['', Validators.required],
    project_uuid: ['', Validators.required]
  });
  submitting = false;
  projects: AutocompleteSuggestion[] = [];

  inContractMode = false;

  accountUuid?: string;

  protected validationSubscription?: Subscription;

  private destroyed$ = new Subject<void>();

  constructor(
    public ref: OverlayAlertRef,
    private overlayAlert: OverlayAlertService,
    private config: OverlayAlertConfig,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private toastService: ToastService,
    private accountService: AccountService,
    private router: Router,
    private route: ActivatedRoute,
    private ngrxService: NgrxService,
    private readonly store: Store
  ) { }


  ngOnInit() {

    this.validationSubscription = this.createForm.statusChanges.subscribe(_status => {
      this.ref.updateButton(1, { disabled: _status !== 'VALID' });
    });

    const location_uuid = this.config.data?.location_uuid;

    this.store.select(selectRouteParam('account_uuid')).pipe(
      takeUntil(this.destroyed$),
      take(1)
    ).subscribe(currentAccountUuid => {
      if (currentAccountUuid !== undefined) {
        this.inContractMode = true;
        this.accountUuid = currentAccountUuid;

        // we only show projects of contract
        this.store.select(AccountSelectors.isAccountLoaded({ uuid: currentAccountUuid })).pipe(
          withLatestFrom(this.store.select(AccountSelectors.isAccountLoading({ uuid: currentAccountUuid }))),
          take(1)
        ).subscribe(([loaded, loading]) => {
          if (!loaded && !loading) {
            this.store.dispatch(AccountActions.loadAccount({ account_uuid: currentAccountUuid }));
          }
        });

        this.store.select(AccountSelectors.getAccount({ uuid: currentAccountUuid })).pipe(
          takeUntil(this.destroyed$),
          filter(account => account?.relations !== undefined),
          take(1)
        ).subscribe(account => {
          this.projects = _.map(account!.relations!.projects, p => ({ value: p.object_uuid, label: p.name }));
        });
      } else {
        this.accountService.isAccountProjectsLoaded$.pipe(
          filter(loaded => !!loaded),
          take(1)
        ).subscribe(() => {
          this.projects = this.accountService.getProjectGroupedByAccount(location_uuid);

          // this.projects.forEach(project => {
          //   project.children?.forEach(childValue => {
          //     childValue.label = project.label;
          //   });
          // });
        });
      }
    });
  }

  onSubmit() {
    if (!this.createForm.invalid) {
      this.submitting = true;
      this.ref.setLoading(true);


      this.ngrxService.callBasicActionWithCallbacks({
        actionToCall: VirtualSwitchActions.createVirtualSwitch({
          name: this.createForm.value?.name,
          project_uuid: this.createForm.value?.project_uuid,
        }),
        successAction: VirtualSwitchActions.createVirtualSwitchSuccess,
        failureAction: VirtualSwitchActions.createVirtualSwitchFailure,
        successCallback: action => {
          this.submitting = false;
          this.ref.setLoading(false);
          if (this.inContractMode) {
            this.router.navigate(['/Accounts', 'SDN', 'VirtualSwitches', action.object_uuid], { relativeTo: this.route, queryParams: { contract: this.accountUuid } });
          } else {
            this.router.navigate(['/Accounts', 'SDN', 'VirtualSwitches', action.object_uuid], { relativeTo: this.route });
          }
          this.ref.close();
          this.toastService.add({
            severity: 'success',
            summary: this.translate.instant('sdn_virtual_switch_create_success')
          });
        },
        failureCallback: () => {
          this.ref.setLoading(false);
        }
      });
    }
  }

  cancel() {
    if (this.submitting === true || this.createForm.dirty) {
      this.overlayAlert.confirm(
        this.translate.instant('DISCARD_CHANGES_HEADLINE'),
        this.translate.instant('DISCARD_CHANGES_INTRO'),
        [
          {
            label: this.translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(false);
            }
          },
          {
            label: this.translate.instant('DISCARD_CHANGES_BTN'),
            mode: 'danger',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(true);
            }
          }
        ],
        'icon-a059_warning')
        .subscribe(_confirm => {
          if (_confirm) {
            this.ref.close();
          } else {
            this.ref.setFocus();
          }
        });
    } else {
      this.ref.close();
    }
  }

  ngOnDestroy() {
    if (this.validationSubscription) {
      this.validationSubscription.unsubscribe();
    }

    this.destroyed$.next();
    this.destroyed$.complete();
  }

}
